import React, { useMemo } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { NotAvailableYet } from '@/components/NotAvailableYet';
import { notAvailableYetHint } from '@/lib/notAvailableYet';
import type { MerchantConnectionsApi } from '@/hooks/useMerchantConnections';
import { ConnectionsOwner, CsvFile, connectionEntries, groupMerchantFeeds } from '@/shared/merchantFeeds';
import { CsvSummaryRow } from './feeds/FeedRows';
import { FeedList } from './feeds/FeedList';
import { SettingsSection } from './SettingsGroup';

// Settings › Pricing Data In › Data Feeds (FIG-321 / REQ-028, REQ-042, REQ-054,
// REQ-056). Replaces the Overview tab's Data Connections card. Bank Feeds (RCO
// accounts on Figabl Inc's Chase connection) need UC-220. The merchant's own
// bank accounts and accounting software are under Licensed Seller Monitoring
// (FIG-323); anything else connected is listed under "Other connections".

interface DataFeedsSectionProps {
  merchant: ConnectionsOwner;
  connections: MerchantConnectionsApi;
  canEdit: boolean;
}

const BANK_FEEDS_USE_CASE = 'UC-220';

export const DataFeedsSection: React.FC<DataFeedsSectionProps> = ({ merchant, connections, canEdit }) => {
  const { toast } = useToast();
  const feeds = useMemo(() => groupMerchantFeeds(merchant.connections), [merchant.connections]);

  const isInheritedFile = (file: CsvFile) =>
    file.importId ? connections.isInheritedImport(file.importId) : connections.isInheritedConnection(file.connectionKey);

  const removeFile = async (file: CsvFile) => {
    if (file.importId) {
      await connections.deleteCsvImport(file.connectionKey, file.importId, file.fileName);
      return;
    }
    const entry = connectionEntries(merchant.connections).find(([key]) => key === file.connectionKey);
    if (!entry) {
      toast({ title: 'Error', description: `${file.fileName} could not be found. Reload the page and try again.`, variant: 'destructive' });
      return;
    }
    await connections.deleteConnection(entry[0], entry[1]);
  };

  const listProps = { owner: merchant, connections, canEdit };

  return (
    <TooltipProvider>
      <SettingsSection
        title="Data Feeds"
        description="The feeds EVE uses for this merchant: bank feeds, inventory feeds and sales transaction feeds. A connection that supplies both inventory and sales appears in both lists."
      >
        <div className="space-y-6">
          <div data-testid="feeds-bank">
            <h4 className="font-medium">Bank Feeds</h4>
            <p className="mb-2 text-sm text-muted-foreground">
              RCO accounts on Figabl Inc's Chase connection, through Plaid. Each RCO account is shared with the purchasing SPV's accounting and with this merchant only. The merchant's own bank accounts are under Licensed Seller Monitoring.
            </p>
            <div className="flex flex-wrap items-center gap-3 rounded-md border border-dashed px-3 py-2">
              <NotAvailableYet useCase={BANK_FEEDS_USE_CASE} />
              {canEdit && (
                <Button type="button" variant="outline" size="sm" disabled className="gap-1.5"
                  aria-label={`Link an RCO account. ${notAvailableYetHint(BANK_FEEDS_USE_CASE)}`}>
                  <Link2 className="h-4 w-4" aria-hidden="true" />
                  Link an RCO account
                </Button>
              )}
            </div>
          </div>

          <div data-testid="feeds-inventory">
            <h4 className="font-medium">Inventory Feeds</h4>
            <p className="mb-2 text-sm text-muted-foreground">Where inventory data is flowing from.</p>
            <FeedList ariaLabel="Inventory feeds" feeds={feeds.inventory} detailFor={() => 'Stock levels'} {...listProps}>
              <CsvSummaryRow
                kind="inventory"
                files={feeds.inventoryCsvFiles}
                canEdit={canEdit}
                removingId={connections.removingId}
                busy={connections.busy}
                isInherited={isInheritedFile}
                onRemoveFile={removeFile}
              />
            </FeedList>
          </div>

          <div data-testid="feeds-sales">
            <h4 className="font-medium">Sales Transaction Feeds</h4>
            <p className="mb-2 text-sm text-muted-foreground">Where sales transaction data is flowing from.</p>
            <FeedList ariaLabel="Sales transaction feeds" feeds={feeds.sales} detailFor={() => 'Orders, refunds and fees'} {...listProps}>
              <CsvSummaryRow
                kind="sales"
                files={feeds.salesCsvFiles}
                canEdit={canEdit}
                removingId={connections.removingId}
                busy={connections.busy}
                isInherited={isInheritedFile}
                onRemoveFile={removeFile}
              />
            </FeedList>
          </div>

          {(feeds.other.length > 0 || feeds.otherCsvFiles.length > 0) && (
            <div data-testid="feeds-other">
              <h4 className="font-medium">Other connections</h4>
              <p className="mb-2 text-sm text-muted-foreground">Connected sources and uploads that are not inventory or sales feeds.</p>
              <FeedList ariaLabel="Other connections" feeds={feeds.other} detailFor={() => 'Fivetran connection'} {...listProps}>
                {feeds.otherCsvFiles.length > 0 && (
                  <CsvSummaryRow
                    kind="other"
                    files={feeds.otherCsvFiles}
                    canEdit={canEdit}
                    removingId={connections.removingId}
                busy={connections.busy}
                    isInherited={isInheritedFile}
                    onRemoveFile={removeFile}
                  />
                )}
              </FeedList>
            </div>
          )}
        </div>
      </SettingsSection>
    </TooltipProvider>
  );
};

export default DataFeedsSection;
